/**
 * Task-related Types
 * Types for task management and status tracking with state machine
 */

/**
 * Task status enumeration
 * Must match the backend Task.TaskStatus enum
 */
export enum TaskStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  ON_HOLD = 'ON_HOLD',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

/**
 * Task status event enumeration
 * Must match the backend TaskStatusEvent enum
 */
export enum TaskStatusEvent {
  CREATED = 'CREATED',
  START_TASK = 'START_TASK',
  PAUSE_TASK = 'PAUSE_TASK',
  RESUME_TASK = 'RESUME_TASK',
  COMPLETE_TASK = 'COMPLETE_TASK',
  CANCEL_TASK = 'CANCEL_TASK'
}

/**
 * Task status history DTO
 * Represents a status change in the task timeline
 */
export interface TaskStatusHistoryDto {
  id: number
  taskId: number
  fromStatus: string | null
  toStatus: string
  changedByLogin: string
  changedByName: string
  changedAt: string
  formattedChangedAt: string
  event: string
  comment?: string
  durationInMillis?: number
  formattedDuration?: string
}

/**
 * Task status change request
 * Used when changing task status through the state machine
 */
export interface TaskStatusChangeRequest {
  newStatus: TaskStatus
  comment?: string
}

/**
 * Task DTO
 * Complete task data transfer object matching backend TaskDto
 */
export interface TaskDto {
  id: number
  projectId: number
  phaseId?: number
  phaseName?: string
  name: string
  description?: string
  amount?: number
  startDate?: string
  endDate?: string
  intervenantId?: number
  intervenantName?: string
  status: TaskStatus
  createdBy?: number
  createdAt?: string
  updatedAt?: string
}

/**
 * Task creation request
 */
export interface TaskCreateRequest {
  projectId: number
  phaseId?: number
  name: string
  description?: string
  amount?: number
  startDate?: string
  endDate?: string
  intervenantId?: number
  status?: TaskStatus
}

/**
 * Task update request
 */
export interface TaskUpdateRequest {
  name?: string
  description?: string
  amount?: number
  startDate?: string
  endDate?: string
  intervenantId?: number
  phaseId?: number
  status?: TaskStatus
}

/**
 * Minimal task info for dropdowns/lists
 */
export interface TaskMinimalDto {
  id: number
  name: string
  intervenantName?: string
}

/**
 * Query parameters for task status history
 */
export interface TaskStatusHistoryQueryParams {
  page?: number
  size?: number
  sort?: string
  direction?: 'ASC' | 'DESC'
}
